import React, { useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  icon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helpText,
  icon,
  showPasswordToggle = false,
  type = 'text',
  className = '',
  ...props
}, ref) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const inputType = showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

  const baseClasses = 'w-full px-4 py-3 rounded-xl border transition-normal ease-smooth focus:outline-none focus-visible-ring';
  
  const stateClasses = error
    ? 'border-red-400/50 text-red-900 placeholder-red-400/50 focus:border-red-400 focus:ring-red-400'
    : isFocused
    ? 'border-gray-300 focus:border-gray-400 focus:ring-gray-300'
    : 'border-gray-200 focus:border-gray-300 focus:ring-gray-200';

  const backgroundClasses = 'glass-input bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500';

  const inputClasses = `${baseClasses} ${stateClasses} ${backgroundClasses} ${icon ? (isRTL ? 'pr-12' : 'pl-12') : ''} ${showPasswordToggle ? (isRTL ? 'pl-12' : 'pr-12') : ''} ${className}`;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium mb-2 opacity-90" style={{color: 'var(--liquid-text-primary)'}}>
          {label}
        </label>
      )}
      
      <div className="relative">
        {icon && (
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none text-brown-400`}>
            {icon}
          </div>
        )}
        
        <input
          ref={ref}
          type={inputType}
          className={inputClasses}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        />
        
        {showPasswordToggle && (
          <button
            type="button"
            className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-brown-400 hover:text-brown-600 dark:hover:text-brown-200`}
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
        
        {error && (
          <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none ${showPasswordToggle ? (isRTL ? 'pl-12' : 'pr-12') : ''}`}>
            <AlertCircle size={20} className="text-red-500" />
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-2 text-sm text-red-400 animate-fade-in">
          {error}
        </p>
      )}
      
      {helpText && !error && (
        <p className="mt-2 text-sm opacity-70" style={{color: 'var(--liquid-text-muted)'}}>
          {helpText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
